import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import SharedBadge from '@/global/Badge/Badge';
import SharedButton from '@/global/Button/Button';
import { getSourceDisplayName } from '@/modules/search/searchGrid/helpers/sourceTabMetadata';
import { buildGtpHeatmapData, tcesToHeatmapColor } from '@/utils/buildGtpHeatmapData';
import { buildAnalyticsSearchTitle } from '../GdaAnalytics/analyticsHelpers';
import { downloadAnalyticsPng } from '../GdaAnalytics/analyticsDownload';
import { ChartPanel } from '../GdaAnalytics/analyticsUi';
import Loader from '../../Common/Loader';
import { NoRecordsFound } from '../NoRecordsFound';
import { GenesGtpClassFilter } from './GenesGtpClassFilter';
import { GenesGtpHeatmapMatrix } from './GenesGtpHeatmapMatrix';
import { useGenesGtpHeatmapData } from './useGenesGtpHeatmapData';
import { trackEvent } from '@/utils/analytics';
import '../GdaAnalytics/GdaAnalytics.css';
import './GenesGtpHeatmap.css';

type GenesGtpHeatmapProps = {
  source: string;
  view: string;
  names: Array<{ id: string; name?: string; label?: string }>;
};

function safeFileStem(title: string): string {
  return `gtp-heatmap-${String(title || 'search')
    .replace(/[^\w.-]+/g, '_')
    .slice(0, 60)}`;
}

export function GenesGtpHeatmap({ source, view, names }: GenesGtpHeatmapProps) {
  const dashboardRef = useRef<HTMLDivElement>(null);
  const [isDownloadingPng, setIsDownloadingPng] = useState(false);
  const { data, isLoading, isEmpty, isPanelActive, searchItems } = useGenesGtpHeatmapData({
    source,
    view,
    names,
  });
  const [hiddenClassNames, setHiddenClassNames] = useState<string[]>([]);
  const searchItemsKey = names.map(item => item.id).join(',');

  useEffect(() => {
    setHiddenClassNames([]);
  }, [searchItemsKey]);

  const heatmap = useMemo(
    () => buildGtpHeatmapData(data, searchItems, hiddenClassNames),
    [data, hiddenClassNames, searchItems],
  );
  const classDotColors = useMemo(
    () =>
      Object.fromEntries(
        heatmap.classNames.map(className => [
          className,
          tcesToHeatmapColor(heatmap.classPeakTces[className] ?? 0, heatmap.maxPositive, heatmap.minNegative),
        ]),
      ),
    [heatmap],
  );
  const searchTitle = buildAnalyticsSearchTitle(names);
  const sourceLabel = getSourceDisplayName(source);

  const toggleClass = (className: string) => {
    trackEvent({
      name: 'gtap_class_filter',
      buttonId: 'genes_gtap_filter_toggle',
      label: className,
      page: 'search_results',
      section: 'graphs',
      feature: 'gtap',
      eventCategory: 'graphs',
    });
    setHiddenClassNames(current =>
      current.includes(className) ? current.filter(name => name !== className) : [...current, className],
    );
  };

  const handleDownloadPng = async () => {
    if (isDownloadingPng) return;
    trackEvent({
      name: 'download_result',
      buttonId: 'genes_gtap_heatmap_download_png',
      label: 'Download PNG',
      page: 'search_results',
      section: 'graphs',
      feature: 'gtap',
      eventCategory: 'graphs',
    });
    setIsDownloadingPng(true);
    try {
      await downloadAnalyticsPng(dashboardRef.current, `${safeFileStem(searchTitle)}.png`);
    } finally {
      setIsDownloadingPng(false);
    }
  };

  if (!isPanelActive || isLoading) {
    return (
      <div className="gda-analytics-loading">
        <Loader />
      </div>
    );
  }

  if (isEmpty || heatmap.genes.length < 2 || names.length < 2) {
    return <NoRecordsFound />;
  }

  return (
    <div ref={dashboardRef} className="gda-analytics-dashboard gtp-heatmap mr-2">
      <header className="gda-analytics-header">
        <div className="gda-analytics-header__title-row">
          <h2 className="gda-analytics-header__title">GTAP</h2>
          <SharedBadge
            text={`Source: ${sourceLabel}`}
            variant="primaryLight"
            size="xs"
            className="gda-analytics-header__source-badge"
          />
        </div>
        <div className="gda-analytics-header__actions">
          <SharedButton
            variant="secondaryOutline"
            size="xs"
            className="gda-analytics-header__download mr-0"
            disabled={isDownloadingPng}
            onClick={handleDownloadPng}
            iconLeft={<ArrowDownTrayIcon className="h-4 w-4" />}
          >
            {isDownloadingPng ? 'Downloading...' : 'Download PNG'}
          </SharedButton>
        </div>
      </header>

      <ChartPanel
        title="Gene Therapeutic Area Profile"
        className="gda-analytics-panel--wide gtp-heatmap-panel"
      >
        <p className="gda-analytics-panel__hint">
          Pills below control which disease classes appear in the heatmap.
        </p>
        <GenesGtpClassFilter
          classNames={heatmap.classNames}
          hiddenClassNames={hiddenClassNames}
          classDotColors={classDotColors}
          onToggle={toggleClass}
          onShowAll={() => {
            trackEvent({
              name: 'gtap_class_filter',
              buttonId: 'genes_gtap_filter_all',
              label: 'All',
              page: 'search_results',
              section: 'graphs',
              feature: 'gtap',
              eventCategory: 'graphs',
            });
            setHiddenClassNames([]);
          }}
          onShowNone={() => {
            trackEvent({
              name: 'gtap_class_filter',
              buttonId: 'genes_gtap_filter_none',
              label: 'None',
              page: 'search_results',
              section: 'graphs',
              feature: 'gtap',
              eventCategory: 'graphs',
            });
            setHiddenClassNames(heatmap.classNames);
          }}
          onReset={() => {
            trackEvent({
              name: 'gtap_class_filter',
              buttonId: 'genes_gtap_filter_reset',
              label: 'Reset',
              page: 'search_results',
              section: 'graphs',
              feature: 'gtap',
              eventCategory: 'graphs',
            });
            setHiddenClassNames([]);
          }}
        />
        {heatmap.series.length === 0 ? (
          <p className="gtp-heatmap__empty" role="status">
            Select at least one disease class to display the heatmap.
          </p>
        ) : (
          <GenesGtpHeatmapMatrix heatmap={heatmap} />
        )}
      </ChartPanel>
    </div>
  );
}
